import {
  AlertTriangle,
  ArrowUpRight,
  Dumbbell,
  RefreshCcw,
  TrendingUp,
  Wifi
} from 'lucide-react'
import iosLaunchHero from '../assets/promos/loadline-ios-launch.png'
import Discord from './icons/Discord'
import { Modal, ModalContent } from './ui/modal'
import { UnifiedButton } from './ui/unified-button'

const FEATURES = [
  {
    icon: Dumbbell,
    title: 'Full workout logging',
    description:
      'Previous weights pre-loaded, built-in rest timer, tap to match your last session. A complete tracker, not just analytics.'
  },
  {
    icon: TrendingUp,
    title: 'Progress tracking',
    description:
      'Estimated 1RM tracked over time with progress charts across 1M to all-time.'
  },
  {
    icon: AlertTriangle,
    title: 'Plateau detection',
    description:
      'Flags lifts that have stalled so you can adjust before progress quietly stops.'
  },
  {
    icon: RefreshCcw,
    title: 'Hevy Import',
    description:
      'Import your Hevy data anytime, as often as you want, without losing the option to use Loadline on its own.'
  },
  {
    icon: Wifi,
    title: 'Works offline',
    description:
      "Log workouts without a connection. Everything syncs automatically when you're back online."
  }
] as const

interface LoadlineIOSPromoModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPrimaryAction: () => void
  onDiscordAction: () => void
}

export function LoadlineIOSPromoModal({
  open,
  onOpenChange,
  onPrimaryAction,
  onDiscordAction
}: LoadlineIOSPromoModalProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange} modalId="loadline-ios-launch-promo">
      <ModalContent className="w-[calc(100%-2rem)] max-w-5xl overflow-hidden p-0">
        <div className="bg-background">
          {/* Hero image — inset */}
          <div className="p-6 pb-0 sm:p-8 sm:pb-0">
            <div className="overflow-hidden rounded-xl bg-[#120e0b]">
              <img
                src={iosLaunchHero}
                alt="Preview of the Loadline iPhone app"
                className="w-full object-contain"
              />
            </div>
          </div>

          {/* Content */}
          <div className="space-y-5 p-6 sm:p-8">
            {/* Title + description */}
            <div>
              <h2 className="text-xl font-bold leading-tight sm:text-2xl">
                Loadline is now its own free workout app.
              </h2>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed sm:text-base">
                Loadline is a full workout tracker with its own logger and the dashboard features
                you already love. Sync your Hevy data into it as many times as you want, or skip
                Hevy entirely and use Loadline on its own.
              </p>
            </div>

            {/* Feature grid */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-x-6 md:gap-y-3 lg:grid-cols-5">
              {FEATURES.map((feature) => (
                <div key={feature.title} className="flex items-start gap-2.5">
                  <feature.icon className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                  <span className="text-foreground text-sm">{feature.title}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-3 border-t pt-5 md:flex-row md:items-center">
              <UnifiedButton
                variant="primary"
                size="lg"
                className="w-full md:w-auto"
                onClick={onPrimaryAction}
              >
                <span className="flex items-center gap-2">
                  Download on the App Store
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </UnifiedButton>
              <UnifiedButton
                variant="ghost"
                size="md"
                className="w-full justify-center text-[#5865F2] hover:text-[#5865F2] md:w-auto"
                onClick={onDiscordAction}
              >
                <span className="flex items-center gap-2">
                  <Discord className="h-4 w-4" />
                  Discord
                </span>
              </UnifiedButton>
              <span className="text-muted-foreground text-left text-xs leading-relaxed md:ml-auto md:max-w-[30%] md:text-right">
                Free with unlimited Hevy syncs. Pro adds full history and deeper insights.
              </span>
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}

interface LoadlineIOSPromoSidebarCardProps {
  onOpenDetails: () => void
}

export function LoadlineIOSPromoSidebarCard({ onOpenDetails }: LoadlineIOSPromoSidebarCardProps) {
  return (
    <div className="bg-sidebar-accent/70 border-sidebar-border overflow-hidden rounded-xl border p-2.5 shadow-sm">
      <div>
        <p className="text-foreground text-sm font-semibold leading-tight">
          New: Loadline for iPhone
        </p>
        <p className="text-muted-foreground mt-0.5 text-xs leading-snug">
          A free full workout tracker. Sync Hevy or skip it entirely.
        </p>
      </div>

      <button
        type="button"
        className="mt-2 block w-full overflow-hidden rounded-lg border"
        onClick={onOpenDetails}
        aria-label="Open details about the new iPhone app"
      >
        <img
          src={iosLaunchHero}
          alt="Preview of the Loadline iPhone app"
          className="h-20 w-full object-cover object-left-top"
        />
      </button>

      <UnifiedButton
        variant="primary"
        size="sm"
        fullWidth
        className="mt-2"
        onClick={onOpenDetails}
      >
        Learn More
      </UnifiedButton>
    </div>
  )
}
